import {
  Box,
  Button,
  Flex,
  Input,
  Textarea,
  Image,
  Text,
  Spinner,
  Skeleton,
  styled,
  Grid,
  useDisclosure
} from "@chakra-ui/react"
import useGridPageStyling from "../hooks/useGridPageStyling"
import {
  ForwardedRef,
  forwardRef,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from "react"
import { motion, useAnimation } from "framer-motion"
import { FaUser } from "react-icons/fa"
import { BackIconButton } from "../../home/HomeBackIcon"
import { MdEdit } from "react-icons/md"
import { GiFairyWand } from "react-icons/gi"
import { MdOutlineFileUpload } from "react-icons/md"
import useWebSocket from "src/hooks/useWebsocket"
import { SDKContext } from "src/context/SDKContext"
import { useRecoilState } from "recoil"
import { authedUserAtom } from "src/recoil/atoms/authedUser"
import useAuthedUser from "src/hooks/useAuthedUser"
import { reactRouterV4BrowserTracingIntegration } from "@sentry/react"
import toast from "react-hot-toast"
import axios from "src/shared/axios"
import { ConsoleLogger } from "src/util/logger"
import { PopupModal } from "../../account/components/PopupModal"
import { t } from "i18next"
import { PlansPageStyling } from "src/context/types"
import usePageStyling from "src/hooks/usePageStyling"

const logger = new ConsoleLogger()

const Form = styled("form")
const Label = styled("label")

const GenerateBtn = ({ onClick }: { onClick: () => void }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="25px"
    h="25px"
    borderRadius="50%"
    color="white"
    bgColor="rgba(128,128,128,1)"
    pos="absolute"
    bottom="-8.5px"
    right="-8.5px"
    onClick={onClick}
    cursor="pointer"
  >
    <MdEdit />
  </Box>
)

const GeneratedImage = ({
  src,
  onClick,
  selected
}: {
  src?: string
  onClick: (url: string) => void
  selected: boolean
}) => {
  const clickRef = useRef<HTMLImageElement>(null!)
  const controls = useAnimation()
  const [srcLoaded, setSrcLoaded] = useState(false)

  const clickHandler = () => {
    controls
      .start({
        scale: 1.6,
        opacity: 0
      })
      .finally(() => {
        controls.set({
          scale: 1,
          opacity: 1
        })
      })
    if (!!src) onClick(src)
  }

  useEffect(() => {
    if (clickRef.current) {
      clickRef.current.onload = () => {
        setSrcLoaded(true)
      }
    }
  }, [clickRef, clickRef.current])

  return (
    <Box
      // w="90px" h="90px"
      w={[90, 150, 150]}
      h={[90, 150, 150]}
      pos="relative"
    >
      {!!src && (
        <>
          <motion.div
            animate={controls}
            transition={{
              ease: "backOut",
              scale: { duration: 0.55 },
              opacity: { duration: 0.36 }
            }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              // background: branding.panelMouseoverBackgroundColor,
              borderRadius: "4px"
            }}
          />
          <Image
            pos="absolute"
            visibility={srcLoaded ? "visible" : "hidden"}
            // w="90px"
            // h="90px"
            w="100%"
            h="100%"
            // w={[120, 250, 250]}
            // h={[120, 250, 250]}
            src={src}
            fit="cover"
            borderRadius="4px"
            cursor="pointer"
            onClick={clickHandler}
            transition="all .15s ease"
            {...(selected
              ? { outline: "2px solid white", transform: "scale(1.03)" }
              : {})}
            ref={clickRef}
          />
        </>
      )}
      {!srcLoaded && (
        <Skeleton position="absolute" w="90px" h="90px" borderRadius="4px" />
      )}
    </Box>
  )
}

const useGenerate = (onClose: (...args: any[]) => unknown) => {
  const meetingClient = useContext(SDKContext)
  const { disconnect, sendMessage, response, inProgress, connect, savedImage } =
    useWebSocket(
      meetingClient?.envConfig.IMAGE_GEN_WEBSOCKET_URL,
      false,
      onClose
    )

  const generate = async (prompt: string) => {
    connect()
    sendMessage(JSON.stringify({ prompt }))
  }

  const saveImage = async (selectedImageUrl: string) => {
    sendMessage(JSON.stringify({ selectedImageUrl }))
  }

  return { generate, response, inProgress, connect, saveImage, savedImage }
}

const GenerateForm = ({
  branding,
  selectedImage,
  setSelectedImage,
  close
}: {
  branding: ReturnType<typeof useGridPageStyling>
  selectedImage: string
  setSelectedImage: (url: string) => void
  close: () => void
}) => {
  const [prompt, setPrompt] = useState("")
  const [numAttempts, setNumAttempts] = useState(0)
  const meetingClient = useContext(SDKContext)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const handleWSClose = (statusCode: number) => {
    if (statusCode === 4000) {
      // some generic error
      toast.error("Something went wrong.")
    } else if (statusCode === 4001) {
      // rate limited, show popup
      onOpen()
    }
  }
  const {
    generate,
    response: images,
    inProgress,
    saveImage,
    savedImage
  } = useGenerate(handleWSClose)
  const upload = () => {
    const input = document.getElementById("image-upload") as HTMLInputElement

    function loadFile(e: ProgressEvent<FileReader>) {
      if (!meetingClient) throw "Meeting client not ready"
      const promise = axios
        .post(
          meetingClient?.envConfig.PERSONAS,
          {
            base64Image: e.target?.result
          },
          { withCredentials: true }
        )
        .then(res => {
          setSelectedImage(res.data.url)
          toast.promise(promise, {
            loading: "Uploading...",
            error: "Something went wrong",
            success: "Image uploaded"
          })
          close()
        })
    }

    function readFile(this: HTMLInputElement) {
      const fr = new FileReader()
      fr.addEventListener("load", loadFile)
      const file = this.files?.item(0)
      if (file) {
        fr.readAsDataURL(file)
      }
    }

    input.addEventListener("change", readFile)

    input.click()
  }

  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const [closeAfterImageSaved, setCloseAfterImageSaved] = useState(false)

  useEffect(() => {
    if (closeAfterImageSaved && savedImage) {
      setSelectedImage(savedImage)
      close()
    }
  }, [savedImage, closeAfterImageSaved])

  return (
    <>
      <PopupModal
        isOpen={isOpen}
        branding={plansPageStyles}
        onClose={onClose}
        title={t("Rate limit exceeded")}
        description={t("Please wait a few moments then try again")}
      />
      <Form
        onSubmit={e => e.preventDefault()}
        display="flex"
        flexDir="column"
        alignItems="center"
        w="100%"
        gap="1rem"
        pos="relative"
        mt="2rem"
      >
        <Input
          id="prompt"
          placeholder="Describe what your persona should look like"
          value={prompt}
          onChange={e => setPrompt(e.currentTarget.value)}
          _active={{}}
          _focus={{}}
          borderColor={"rgb(255 255 255 / 60%)"}
          bg={"rgba(0, 0, 0, 0.8)"}
        />
        <Button
          disabled={!prompt.length || numAttempts >= 2}
          onClick={() => {
            generate(prompt)
            // setNumAttempts(prev => prev + 1)
          }}
          bgColor={branding.buttonBackground}
          color={branding.buttonText}
          _hover={{
            bgColor: branding.buttonBackgroundHover,
            color: branding.buttonTextHover
          }}
          _disabled={{
            opacity: 0.6
          }}
          _active={{}}
          _focus={{}}
          type="submit"
          width="100%"
        >
          {inProgress && (
            <>
              <Spinner size="xs" />
              &nbsp;&nbsp;
            </>
          )}
          <GiFairyWand />
          &nbsp;&nbsp;Generate
        </Button>
        {(inProgress || !!images.length) && (
          <Grid
            gridTemplateColumns={["repeat(2, 1fr)", "repeat(4, 1fr)"]}
            gap="10px"
            my="10px"
          >
            {(!!images.length ? images : Array.from(Array(4))).map(
              (image, index) => {
                return (
                  <GeneratedImage
                    key={`${index}-${numAttempts}`}
                    src={image ?? undefined}
                    selected={image === selectedImage}
                    onClick={img =>
                      setSelectedImage(img === selectedImage ? "" : img)
                    }
                  />
                )
              }
            )}
          </Grid>
        )}
        {!!images.length && (
          <Button
            onClick={() => {
              setCloseAfterImageSaved(true)
              saveImage(selectedImage)
            }}
            disabled={
              !images.length || !images.some(url => url === selectedImage)
            }
            bgColor={branding.buttonBackground}
            color={branding.buttonText}
            _hover={{
              bgColor: branding.buttonBackgroundHover,
              color: branding.buttonTextHover
            }}
            _disabled={{
              opacity: 0.6
            }}
            _active={{}}
            _focus={{}}
            display="block"
            margin="0 auto"
            width="100%"
          >
            Use Photo
          </Button>
        )}
        <Box
          marginY={"10px"}
          width={"80%"}
          height="1px"
          background={"white"}
          rounded="full"
        />
        <Text mt="">Or you can upload your own</Text>
        <Button
          onClick={() => upload()}
          bgColor={branding.buttonBackground}
          color={branding.buttonText}
          _hover={{
            bgColor: branding.buttonBackgroundHover,
            color: branding.buttonTextHover
          }}
          _disabled={{
            opacity: 0.6
          }}
          _active={{}}
          _focus={{}}
          type="submit"
          width="100%"
        >
          <MdOutlineFileUpload />
          &nbsp;&nbsp;Upload Image
        </Button>
        <input
          id="image-upload"
          type="file"
          accept="image/*"
          style={{ display: "none" }}
        />
      </Form>
    </>
  )
}

const UserPersonaImage = ({
  bgColor,
  src,
  onClick
}: {
  bgColor: string
  src?: string
  onClick: () => void
}) => {
  return (
    <Box
      pos="relative"
      // w="120px"
      // h="120px"
      w={[120, 300, 300]}
      h={[120, 300, 300]}
      borderRadius="6px"
      bgColor={bgColor}
      cursor="pointer"
      onClick={onClick}
    >
      <GenerateBtn onClick={onClick} />
      <Box w="100%" h="100%" overflow="hidden" borderRadius="6px">
        {src && <Image src={src} fit="cover" w="100%" h="100%" />}
        <Flex
          pos="absolute"
          top={0}
          w="100%"
          h="100%"
          align="center"
          justify="center"
        >
          {!src?.length && (
            <FaUser color="rgba(210, 210, 210, 0.65)" size={60} />
          )}
        </Flex>
      </Box>
    </Box>
  )
}

type State = {
  screenName: string
  description: string
  screenNameLength: number
  descriptionLength: number
  imageUrl?: string
}

type Action = { type: "set"; value: Partial<State> }
const defaultValues: State = {
  screenName: "",
  description: "",
  descriptionLength: 0,
  screenNameLength: 0
}
type Values = typeof defaultValues
type LengthValues = { screenNameLength: number; descriptionLength: number }
type Dispatch = (action: {
  value: Partial<Values & LengthValues>
  type: "set"
}) => void

const EditForm = ({
  branding,
  imageHasBeenSelected,
  onSave,
  state,
  dispatch
}: {
  branding: ReturnType<typeof useGridPageStyling>
  imageHasBeenSelected: boolean
  onSave: () => void
  state: Values & LengthValues
  dispatch: Dispatch
}) => {
  const MAX_SCREEN_NAME_LENGTH = 20
  const MAX_DESCRIPTION_LENGTH = 500

  return (
    <>
      <Flex w="100%" alignItems="flex-end" flexDir="column" align="center">
        <Label
          htmlFor="screenname"
          fontSize="20px"
          fontWeight={"medium"}
          w="100%"
        >
          Screen Name
          <Input
            id="screenname"
            placeholder="Name your persona"
            value={state.screenName}
            onChange={e => {
              const value = e.currentTarget.value.slice(
                0,
                MAX_SCREEN_NAME_LENGTH
              )
              dispatch({
                type: "set",
                value: {
                  screenName: value,
                  screenNameLength: value.length
                }
              })
            }}
            _active={{}}
            _focus={{}}
            bg={"rgba(0, 0, 0, 0.8)"}
            borderColor={"rgb(255 255 255 / 60%)"}
          />
        </Label>
        <Text color={branding.panelTextColor} fontSize="14px" mt=".5rem">{`${
          state.screenNameLength ?? 0
        }/${MAX_SCREEN_NAME_LENGTH}`}</Text>
      </Flex>
      <Flex w="100%" alignItems="flex-end" flexDir="column">
        <Label
          htmlFor="description"
          fontSize="20px"
          fontWeight={"medium"}
          w="100%"
        >
          Description
          <Textarea
            id="description"
            placeholder="Describe your persona. The more information you provide, the better your conversations will be."
            value={state.description}
            onChange={e => {
              const value = e.currentTarget.value.slice(
                0,
                MAX_DESCRIPTION_LENGTH
              )
              dispatch({
                type: "set",
                value: {
                  description: value,
                  descriptionLength: value.length
                }
              })
            }}
            bg={"rgba(0, 0, 0, 0.8)"}
            _active={{}}
            _focus={{}}
            resize="none"
            height="125px"
            borderColor={"rgb(255 255 255 / 60%)"}
          />
        </Label>
        <Text color={branding.panelTextColor} fontSize="14px" mt=".5rem">{`${
          state.descriptionLength ?? 0
        }/${MAX_DESCRIPTION_LENGTH}`}</Text>
      </Flex>
      <Button
        disabled={!state.screenName}
        onClick={() => onSave()}
        bgColor={branding.buttonBackground}
        color={branding.buttonText}
        _hover={{
          bgColor: branding.buttonBackgroundHover,
          color: branding.buttonTextHover
        }}
        _disabled={{
          opacity: 0.6
        }}
        _active={{}}
        _focus={{}}
        position="relative"
        top="1rem"
        display="block"
        margin="0 auto"
        width="100%"
      >
        Save Changes
      </Button>
    </>
  )
}

const CreatePersona = ({
  setShowGenerateForm,
  showGenerateForm
}: {
  setShowGenerateForm: (value: SetStateAction<boolean>) => void
  showGenerateForm: boolean
}) => {
  const branding = useGridPageStyling()

  const { getAuthedUser, updateAuthedUserAsync } = useAuthedUser()

  const loadActivePersona = async () => {
    const user = await getAuthedUser()
    type PersonaModel = {
      personaId: number
      screenName: string
      description: string
      imageUrl: string
      ownerId: number
    }
    if (!meetingClient) return
    const res = await axios.get<PersonaModel[]>(
      meetingClient?.envConfig.PERSONAS,
      {
        withCredentials: true
      }
    )
    if (!res || res.headers["content-type"] !== "application/json") {
      // use the screen name entered on the join page if no persona is available
      return
    }
    const persona = res?.data?.find(
      persona => persona.personaId === user.personaId
    )
    if (persona) {
      dispatch({
        type: "set",
        value: {
          ...persona,
          screenNameLength: persona.screenName.length,
          descriptionLength: persona.description.length
        }
      })
    } else {
      dispatch({
        type: "set",
        value: {
          screenName: user.screenName
        }
      })
    }
  }

  useEffect(() => {
    loadActivePersona()
  }, [])

  const [selectedImage, setSelectedImage] = useState<string>("")
  const meetingClient = useContext(SDKContext)

  const [state, dispatch] = useReducer(
    (state: State, action: Action): State => {
      switch (action.type) {
        case "set":
          return { ...state, ...action.value }
      }
      return state
    },
    defaultValues
  )

  const onSave = async () => {
    try {
      if (!meetingClient) throw "Meeting client not ready"
      const authedUser = await getAuthedUser()

      const imageUrl = !!selectedImage ? selectedImage : state?.imageUrl ?? null

      const promise = axios
        .post(
          meetingClient?.envConfig.PERSONAS,
          {
            screenName: state.screenName,
            description: state.description ?? "",
            imageUrl,
            ownerId: authedUser.userId
          },
          { withCredentials: true }
        )
        .then(res => {
          updateAuthedUserAsync({ personaId: res.data.personaId })
        })
      toast.promise(promise, {
        loading: "Updating...",
        error: "Something went wrong",
        success: "Persona set"
      })
    } catch (err) {
      logger.error(err)
    }
  }

  return (
    <Flex
      flexDir={"column"}
      color={branding.panelTextColor}
      height="100%"
      align="center"
      w={["100%", null, "60%"]}
      paddingTop={"19px"}
      margin="0 auto"
    >
      {showGenerateForm ? (
        <>
          <GenerateForm
            branding={branding}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            close={() => {
              setShowGenerateForm(false)
            }}
          />
        </>
      ) : (
        <Flex gap="1rem" flexDir="column" align="center" w="100%">
          <UserPersonaImage
            bgColor={branding.panelBackgroundColor}
            src={selectedImage ? selectedImage : state?.imageUrl}
            onClick={() => {
              setShowGenerateForm(true)
            }}
          />
          <Box w="100%" pb="3rem">
            <EditForm
              branding={branding}
              onSave={onSave}
              state={state}
              dispatch={dispatch}
              imageHasBeenSelected={selectedImage.length > 0}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  )
}

export default CreatePersona
